import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import {
  authMutedLinkClassName,
  fieldErrorId,
  FieldLabelRow,
  focusFirstInvalidField,
  FormValidation,
  getEmailError,
} from '~/client/src/shared/components/AuthLayout'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Field,
  FieldGroup,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import Localization from '~/client/src/shared/localization/LocalizationManager'

const EMAIL_INPUT_ID = 'login-email'
const PASSWORD_INPUT_ID = 'login-password'

type LoginField = 'email' | 'password'

const INPUT_IDS: Record<LoginField, string> = {
  email: EMAIL_INPUT_ID,
  password: PASSWORD_INPUT_ID,
}

interface IProps {
  onSubmit: (email: string, password: string) => void
  onResetPassword: (email: string) => void
  onEmailBlur?: (email: string) => void
}

@observer
export default class EmailPasswordForm extends React.Component<IProps> {
  @observable private email: string = ''
  @observable private password: string = ''

  private readonly validation = new FormValidation(() => this.errors)

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const emailError = this.validation.errorOf('email')
    const passwordError = this.validation.errorOf('password')

    return (
      <form
        noValidate
        className="tw:flex tw:flex-col tw:gap-6"
        onSubmit={this.handleSubmit}
      >
        <FieldGroup className="tw:gap-4">
          <Field data-invalid={!!emailError}>
            <FieldLabelRow htmlFor={EMAIL_INPUT_ID} error={emailError}>
              {Localization.translator.email_noun}
            </FieldLabelRow>
            <Input
              id={EMAIL_INPUT_ID}
              type="email"
              name="email"
              required
              autoComplete="username email"
              value={this.email}
              onChange={this.handleEmailChange}
              onBlur={this.handleEmailBlur}
              aria-invalid={!!emailError || undefined}
              aria-describedby={
                emailError ? fieldErrorId(EMAIL_INPUT_ID) : undefined
              }
              data-testid="login-email-input"
            />
          </Field>
          <Field data-invalid={!!passwordError}>
            <FieldLabelRow htmlFor={PASSWORD_INPUT_ID} error={passwordError}>
              {Localization.translator.password}
            </FieldLabelRow>
            <Input
              id={PASSWORD_INPUT_ID}
              type="password"
              name="password"
              required
              autoComplete="current-password"
              value={this.password}
              onChange={this.handlePasswordChange}
              onBlur={this.handlePasswordBlur}
              aria-invalid={!!passwordError || undefined}
              aria-describedby={
                passwordError ? fieldErrorId(PASSWORD_INPUT_ID) : undefined
              }
              data-testid="login-password-input"
            />
          </Field>
          <div className="tw:flex tw:justify-end">
            <Button
              type="button"
              variant="link"
              className={authMutedLinkClassName}
              onClick={() => this.props.onResetPassword(this.email)}
              data-testid="login-forgot-password-link"
            >
              {Localization.translator.forgotPassword}
            </Button>
          </div>
        </FieldGroup>

        <Button
          type="submit"
          className="tw:w-full"
          data-testid="login-sign-in-button"
        >
          {Localization.translator.signIn}
        </Button>
      </form>
    )
  }

  @computed
  private get errors(): Record<LoginField, string> {
    const { authFieldErrors } = Localization.translator

    return {
      email: getEmailError(this.email),
      password: this.password ? '' : authFieldErrors.enterYourPassword,
    }
  }

  @action.bound
  private handleEmailChange(evt: React.SyntheticEvent<HTMLInputElement>) {
    this.email = evt.currentTarget.value
  }

  @action.bound
  private handlePasswordChange(evt: React.SyntheticEvent<HTMLInputElement>) {
    this.password = evt.currentTarget.value
  }

  private handleSubmit = (evt: React.SyntheticEvent<HTMLFormElement>) => {
    evt.preventDefault()
    if (
      focusFirstInvalidField(
        this.validation.attemptSubmit,
        field => INPUT_IDS[field],
      )
    ) {
      return
    }
    this.props.onSubmit(this.email, this.password)
  }

  private handleEmailBlur = () => {
    this.validation.touch('email', this.email)
    this.props.onEmailBlur?.(this.email)
  }

  private handlePasswordBlur = () => {
    this.validation.touch('password', this.password)
  }
}
